import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type {
  AssignmentV3ConditionMode,
  AssignmentV3Condition as AssignmentV3ConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import { AssignmentTypeOption } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { GetUser } from "@redotech/redo-model/user";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { catchApiErrors } from "../../../../../../support-v2/db/error-handling";

type ModeTag = AssignmentV3ConditionMode["_tag"];

const modeOptions: RedoListItem<ModeTag>[] = [
  { id: "type", type: "text", text: "Type", value: "type" },
  {
    id: "specific_users",
    type: "text",
    text: "Specific users",
    value: "specific_users",
  },
  { id: "user_group", type: "text", text: "User groups", value: "user_group" },
];

const assignmentTypeOptions: BadgeRedoListItem<AssignmentTypeOption>[] = [
  {
    id: AssignmentTypeOption.AI_AGENT_ASSIGNEE,
    type: "badge",
    value: AssignmentTypeOption.AI_AGENT_ASSIGNEE,
    badge: { text: AssignmentTypeOption.AI_AGENT_ASSIGNEE },
  },
  {
    id: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
    type: "badge",
    value: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
    badge: { text: AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE },
  },
  {
    id: AssignmentTypeOption.NO_ASSIGNEE,
    type: "badge",
    value: AssignmentTypeOption.NO_ASSIGNEE,
    badge: { text: AssignmentTypeOption.NO_ASSIGNEE },
  },
];

export const AssignmentCondition = memo(function AssignmentCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: AssignmentV3ConditionType;
  onChange: (updated: AssignmentV3ConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const team = useRequiredContext(TeamContext);
  const effectApi = useRequiredContext(EffectApiClientContext);

  const isEditable = (field: string) =>
    editableFields === undefined || editableFields.has(field);
  // The mode discriminant (`_tag`) is editable when the template binds the
  // whole `mode` object. The inner field (assignment types / users / groups)
  // is editable when either `mode` itself is bound or the relevant inner
  // dotted path is bound — e.g. a default rule that fixes the mode to `type`
  // but lets the merchant pick which `assignmentTypes` to match against.
  const modeTagEditable = isEditable("mode");
  const innerFieldName =
    condition.mode._tag === "type"
      ? "mode.assignmentTypes"
      : condition.mode._tag === "specific_users"
        ? "mode.mongoUserIds"
        : "mode.userGroupIds";
  const innerFieldEditable = modeTagEditable || isEditable(innerFieldName);

  const userOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (team?.users || []).map((teamUser) => {
      const user = teamUser.user as GetUser;
      return {
        id: user._id,
        type: "badge" as const,
        value: user._id,
        badge: { text: user.name || user._id },
      };
    });
  }, [team?.users]);

  const userGroupsLoad = useLoad(async () => {
    const client = effectApi.getClient();
    const result = await effectApi.runPromise(
      catchApiErrors(client.userGroup.listUserGroups({})),
    );
    return result.groups;
  }, [effectApi]);

  const groupOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const groups = userGroupsLoad.value ?? [];
    return groups.map((group) => ({
      id: group.id,
      type: "badge" as const,
      value: group.id,
      badge: { text: group.name },
    }));
  }, [userGroupsLoad.value]);

  const [usersDropdownRef, setUsersDropdownRef] =
    useState<HTMLButtonElement | null>(null);
  const [usersDropdownOpen, setUsersDropdownOpen] = useState(false);
  const [groupsDropdownRef, setGroupsDropdownRef] =
    useState<HTMLButtonElement | null>(null);
  const [groupsDropdownOpen, setGroupsDropdownOpen] = useState(false);
  const [typesDropdownRef, setTypesDropdownRef] =
    useState<HTMLButtonElement | null>(null);
  const [typesDropdownOpen, setTypesDropdownOpen] = useState(false);

  const selectedUsers: BadgeRedoListItem<string>[] = useMemo(() => {
    if (condition.mode._tag !== "specific_users") return [];
    const selected = new Set(condition.mode.mongoUserIds);
    return userOptions.filter((opt) => selected.has(opt.value));
  }, [condition.mode, userOptions]);

  const selectedGroups: BadgeRedoListItem<string>[] = useMemo(() => {
    if (condition.mode._tag !== "user_group") return [];
    const selected = new Set(condition.mode.userGroupIds);
    return groupOptions.filter((opt) => selected.has(opt.value));
  }, [condition.mode, groupOptions]);

  const selectedTypes: BadgeRedoListItem<AssignmentTypeOption>[] =
    useMemo(() => {
      if (condition.mode._tag !== "type") return [];
      const selected = new Set(condition.mode.assignmentTypes);
      return assignmentTypeOptions.filter((opt) => selected.has(opt.value));
    }, [condition.mode]);

  const selectedModeOption = modeOptions.find(
    (opt) => opt.value === condition.mode._tag,
  );

  const handleModeChange = (newMode: ModeTag) => {
    if (newMode === condition.mode._tag) return;
    switch (newMode) {
      case "type":
        onChange({ ...condition, mode: { _tag: "type", assignmentTypes: [] } });
        break;
      case "specific_users":
        onChange({
          ...condition,
          mode: { _tag: "specific_users", mongoUserIds: [] },
        });
        break;
      case "user_group":
        onChange({
          ...condition,
          mode: { _tag: "user_group", userGroupIds: [] },
        });
        break;
      default:
        assertNever(newMode);
    }
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<ModeTag>
        disabled={!modeTagEditable}
        fitToAnchor
        label="Assignment"
        options={modeOptions}
        optionSelected={({ item }) => handleModeChange(item.value)}
        selectedItem={selectedModeOption}
      />

      {condition.mode._tag === "type" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Types ({selectedTypes.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!innerFieldEditable}
            isDropdownOpen={typesDropdownOpen}
            onClick={() =>
              innerFieldEditable && setTypesDropdownOpen(!typesDropdownOpen)
            }
            placeholder="Select types..."
            ref={setTypesDropdownRef}
            removeItem={(item) => {
              if (!innerFieldEditable || condition.mode._tag !== "type") return;
              onChange({
                ...condition,
                mode: {
                  _tag: "type",
                  assignmentTypes: condition.mode.assignmentTypes.filter(
                    (t) => t !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedTypes}
            size="xs"
          />
          <RedoMultiselectDropdown<AssignmentTypeOption>
            dropdownAnchor={typesDropdownRef}
            dropdownOpen={typesDropdownOpen}
            fitToAnchor
            items={assignmentTypeOptions}
            selectedItems={selectedTypes}
            selectionVariant="checkbox"
            setDropdownOpen={setTypesDropdownOpen}
            setSelectedItems={(items) => {
              if (!innerFieldEditable) return;
              onChange({
                ...condition,
                mode: {
                  _tag: "type",
                  assignmentTypes: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {condition.mode._tag === "specific_users" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Users ({selectedUsers.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!innerFieldEditable}
            isDropdownOpen={usersDropdownOpen}
            onClick={() =>
              innerFieldEditable && setUsersDropdownOpen(!usersDropdownOpen)
            }
            placeholder="Select users..."
            ref={setUsersDropdownRef}
            removeItem={(item) => {
              if (
                !innerFieldEditable ||
                condition.mode._tag !== "specific_users"
              )
                return;
              onChange({
                ...condition,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: condition.mode.mongoUserIds.filter(
                    (id) => id !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedUsers}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={usersDropdownRef}
            dropdownOpen={usersDropdownOpen}
            fitToAnchor
            items={userOptions}
            selectedItems={selectedUsers}
            selectionVariant="checkbox"
            setDropdownOpen={setUsersDropdownOpen}
            setSelectedItems={(items) => {
              if (!innerFieldEditable) return;
              onChange({
                ...condition,
                mode: {
                  _tag: "specific_users",
                  mongoUserIds: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}

      {condition.mode._tag === "user_group" && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Groups ({selectedGroups.length})
          </Text>
          <RedoTagsDropdownInputButton
            disabled={!innerFieldEditable}
            isDropdownOpen={groupsDropdownOpen}
            onClick={() =>
              innerFieldEditable && setGroupsDropdownOpen(!groupsDropdownOpen)
            }
            placeholder={
              userGroupsLoad.pending ? "Loading groups..." : "Select groups..."
            }
            ref={setGroupsDropdownRef}
            removeItem={(item) => {
              if (!innerFieldEditable || condition.mode._tag !== "user_group")
                return;
              onChange({
                ...condition,
                mode: {
                  _tag: "user_group",
                  userGroupIds: condition.mode.userGroupIds.filter(
                    (id) => id !== item.value,
                  ),
                },
              });
            }}
            selectedItems={selectedGroups}
            size="xs"
          />
          <RedoMultiselectDropdown<string>
            dropdownAnchor={groupsDropdownRef}
            dropdownOpen={groupsDropdownOpen}
            fitToAnchor
            items={groupOptions}
            selectedItems={selectedGroups}
            selectionVariant="checkbox"
            setDropdownOpen={setGroupsDropdownOpen}
            setSelectedItems={(items) => {
              if (!innerFieldEditable) return;
              onChange({
                ...condition,
                mode: {
                  _tag: "user_group",
                  userGroupIds: items.map((item) => item.value),
                },
              });
            }}
            size="xs"
          />
        </Flex>
      )}
    </Flex>
  );
});
